import React, { useState, useEffect } from 'react'
import {Plus, Edit2, Trash2, Save, X, ChevronUp, ChevronDown} from 'lucide-react'
import { lumi } from '../lib/lumi'

interface QuizQuestion {
  _id: string
  question: string
  options: Array<{ text: string; score: number }>
  order: number
  category?: string
  active: boolean
  createdAt: string
  updatedAt: string
}

interface QuizQuestionsManagerProps {
  onClose: () => void
  embedded?: boolean
}

export function QuizQuestionsManager({ onClose, embedded }: QuizQuestionsManagerProps) {
  const [questions, setQuestions] = useState<QuizQuestion[]>([])
  const [loading, setLoading] = useState(true)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [showAddForm, setShowAddForm] = useState(false)
  const [formData, setFormData] = useState({
    question: '',
    category: '',
    options: [
      { text: '', score: 0 },
      { text: '', score: 25 },
      { text: '', score: 50 },
      { text: '', score: 100 }
    ]
  })

  useEffect(() => {
    fetchQuestions()
  }, [])

  const fetchQuestions = async () => {
    setLoading(true)
    try {
      const result = await lumi.entities.quiz_questions.list({
        sort: { order: 1 }
      })
      setQuestions(result.list || [])
    } catch (error) {
      console.error('Error fetching questions:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleAddQuestion = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      const maxOrder = questions.length > 0 ? Math.max(...questions.map(q => q.order)) : 0
      await lumi.entities.quiz_questions.create({
        question: formData.question,
        category: formData.category,
        options: formData.options,
        order: maxOrder + 1,
        active: true,
        creator: lumi.auth.user?.userId || 'admin',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      })
      await fetchQuestions()
      resetForm()
    } catch (error) {
      console.error('Error adding question:', error)
      alert('❌ Erreur lors de l\'ajout de la question')
    }
  }

  const handleUpdateQuestion = async (questionId: string) => {
    try {
      const question = questions.find(q => q._id === questionId)
      if (!question) return

      await lumi.entities.quiz_questions.update(questionId, {
        question: formData.question,
        category: formData.category,
        options: formData.options,
        updatedAt: new Date().toISOString()
      })
      await fetchQuestions()
      setEditingId(null)
      resetForm()
    } catch (error) {
      console.error('Error updating question:', error)
      alert('❌ Erreur lors de la mise à jour')
    }
  }

  const handleDeleteQuestion = async (questionId: string) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer cette question ?')) return
    try {
      await lumi.entities.quiz_questions.delete(questionId)
      await fetchQuestions()
    } catch (error) {
      console.error('Error deleting question:', error)
      alert('❌ Erreur lors de la suppression')
    }
  }

  const handleMoveQuestion = async (questionId: string, direction: 'up' | 'down') => {
    const currentIndex = questions.findIndex(q => q._id === questionId)
    if (currentIndex === -1) return
    if (direction === 'up' && currentIndex === 0) return
    if (direction === 'down' && currentIndex === questions.length - 1) return

    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1
    const currentQuestion = questions[currentIndex]
    const targetQuestion = questions[targetIndex]

    try {
      await Promise.all([
        lumi.entities.quiz_questions.update(currentQuestion._id, {
          order: targetQuestion.order,
          updatedAt: new Date().toISOString()
        }),
        lumi.entities.quiz_questions.update(targetQuestion._id, {
          order: currentQuestion.order,
          updatedAt: new Date().toISOString()
        })
      ])
      await fetchQuestions()
    } catch (error) {
      console.error('Error moving question:', error)
    }
  }

  const startEdit = (question: QuizQuestion) => {
    setEditingId(question._id)
    setFormData({
      question: question.question,
      category: question.category || '',
      options: question.options
    })
    setShowAddForm(false)
  }

  const resetForm = () => {
    setFormData({
      question: '',
      category: '',
      options: [
        { text: '', score: 0 },
        { text: '', score: 25 },
        { text: '', score: 50 },
        { text: '', score: 100 }
      ]
    })
    setShowAddForm(false)
    setEditingId(null)
  }

  const handleOptionChange = (index: number, field: 'text' | 'score', value: string | number) => {
    const newOptions = [...formData.options]
    newOptions[index] = { ...newOptions[index], [field]: value }
    setFormData({ ...formData, options: newOptions })
  }

  const addOption = () => {
    setFormData({
      ...formData,
      options: [...formData.options, { text: '', score: 50 }]
    })
  }

  const removeOption = (index: number) => {
    if (formData.options.length <= 2) {
      alert('Une question doit avoir au moins 2 options')
      return
    }
    const newOptions = formData.options.filter((_, i) => i !== index)
    setFormData({ ...formData, options: newOptions })
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600">Chargement des questions...</div>
      </div>
    )
  }

  return (
    <div className={embedded ? '' : 'fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4'}>
      <div className={embedded ? 'w-full' : 'bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto'}>
        {!embedded && (
          <div className="sticky top-0 bg-gradient-to-r from-teal-600 to-cyan-600 text-white p-6 rounded-t-2xl flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold">Gestion des Questions du Quiz</h2>
              <p className="text-teal-100 text-sm">Ajoutez, modifiez ou réorganisez les questions</p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg transition-colors">
              <X className="w-6 h-6" />
            </button>
          </div>
        )}

        <div className="p-6">
          {/* Add Button */}
          <div className="mb-6">
            <button
              onClick={() => {
                setShowAddForm(!showAddForm)
                setEditingId(null)
                resetForm()
              }}
              className="flex items-center gap-2 bg-gradient-to-r from-teal-600 to-cyan-600 text-white px-6 py-3 rounded-full font-semibold hover:shadow-lg hover:scale-105 transition-all"
            >
              <Plus className="w-5 h-5" />
              Ajouter une question
            </button>
          </div>

          {/* Add/Edit Form */}
          {(showAddForm || editingId) && (
            <div className="bg-gray-50 border-2 border-teal-600 rounded-xl p-6 mb-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                {editingId ? 'Modifier la question' : 'Nouvelle question'}
              </h3>
              <form onSubmit={editingId ? (e) => { e.preventDefault(); handleUpdateQuestion(editingId) } : handleAddQuestion} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Question *</label>
                  <textarea
                    required
                    value={formData.question}
                    onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-teal-600 focus:outline-none resize-none"
                    rows={3}
                    placeholder="Entrez votre question..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Catégorie</label>
                  <input
                    type="text"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-teal-600 focus:outline-none"
                    placeholder="Ex: pilotage, stratégie, équilibre..."
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="block text-sm font-medium text-gray-700">
                      Options de réponse (Score : 0 = besoin critique, 100 = autonome)
                    </label>
                    <button
                      type="button"
                      onClick={addOption}
                      className="text-teal-600 hover:text-teal-700 text-sm font-medium"
                    >
                      + Ajouter une option
                    </button>
                  </div>
                  
                  {/* Légende des tranches */}
                  <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <p className="text-xs font-medium text-gray-600 mb-2">Tranches de score recommandées :</p>
                    <div className="grid grid-cols-4 gap-2 text-xs">
                      <div className="flex items-center gap-1">
                        <span className="w-3 h-3 rounded-full bg-red-500"></span>
                        <span className="text-gray-700">0-25 : Critique</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="w-3 h-3 rounded-full bg-orange-500"></span>
                        <span className="text-gray-700">26-50 : Modéré</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="w-3 h-3 rounded-full bg-blue-500"></span>
                        <span className="text-gray-700">51-75 : Satisfaisant</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="w-3 h-3 rounded-full bg-green-500"></span>
                        <span className="text-gray-700">76-100 : Autonome</span>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    {formData.options.map((option, index) => (
                      <div key={index} className="space-y-2">
                        <div className="flex gap-2">
                          <input
                            type="text"
                            required
                            value={option.text}
                            onChange={(e) => handleOptionChange(index, 'text', e.target.value)}
                            className="flex-1 px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-teal-600 focus:outline-none"
                            placeholder={`Option ${index + 1}`}
                          />
                          <input
                            type="number"
                            required
                            min="0"
                            max="100"
                            value={option.score}
                            onChange={(e) => handleOptionChange(index, 'score', parseInt(e.target.value))}
                            className="w-24 px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-teal-600 focus:outline-none text-center font-semibold"
                          />
                          {formData.options.length > 2 && (
                            <button
                              type="button"
                              onClick={() => removeOption(index)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          )}
                        </div>
                        {/* Boutons de sélection rapide par tranche */}
                        <div className="flex gap-1 ml-1">
                          <button
                            type="button"
                            onClick={() => handleOptionChange(index, 'score', 0)}
                            className="px-2 py-1 text-xs rounded bg-red-100 hover:bg-red-200 text-red-700 font-medium transition-colors"
                          >
                            0
                          </button>
                          <button
                            type="button"
                            onClick={() => handleOptionChange(index, 'score', 25)}
                            className="px-2 py-1 text-xs rounded bg-orange-100 hover:bg-orange-200 text-orange-700 font-medium transition-colors"
                          >
                            25
                          </button>
                          <button
                            type="button"
                            onClick={() => handleOptionChange(index, 'score', 50)}
                            className="px-2 py-1 text-xs rounded bg-yellow-100 hover:bg-yellow-200 text-yellow-700 font-medium transition-colors"
                          >
                            50
                          </button>
                          <button
                            type="button"
                            onClick={() => handleOptionChange(index, 'score', 75)}
                            className="px-2 py-1 text-xs rounded bg-blue-100 hover:bg-blue-200 text-blue-700 font-medium transition-colors"
                          >
                            75
                          </button>
                          <button
                            type="button"
                            onClick={() => handleOptionChange(index, 'score', 100)}
                            className="px-2 py-1 text-xs rounded bg-green-100 hover:bg-green-200 text-green-700 font-medium transition-colors"
                          >
                            100
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="submit"
                    className="flex-1 flex items-center justify-center gap-2 bg-gradient-to-r from-teal-600 to-cyan-600 text-white px-6 py-3 rounded-full font-semibold hover:shadow-lg hover:scale-105 transition-all"
                  >
                    <Save className="w-5 h-5" />
                    {editingId ? 'Mettre à jour' : 'Ajouter'}
                  </button>
                  <button
                    type="button"
                    onClick={resetForm}
                    className="flex-1 bg-white text-gray-800 px-6 py-3 rounded-full font-semibold border-2 border-gray-200 hover:border-gray-300 transition-all"
                  >
                    Annuler
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Questions List */}
          <div className="space-y-4">
            {questions.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                Aucune question pour le moment. Ajoutez-en une !
              </div>
            ) : (
              questions.map((question, index) => (
                <div key={question._id} className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-teal-600 transition-colors">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="bg-teal-100 text-teal-700 px-3 py-1 rounded-full text-sm font-semibold">
                          Q{question.order}
                        </span>
                        {question.category && (
                          <span className="bg-gray-100 text-gray-600 px-3 py-1 rounded-full text-sm">
                            {question.category}
                          </span>
                        )}
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 mb-3">{question.question}</h3>
                      <div className="space-y-2">
                        {question.options.map((option, optIndex) => (
                          <div key={optIndex} className="flex items-center gap-3 text-sm">
                            <span className={`px-2 py-1 rounded font-semibold ${
                              option.score === 0 ? 'bg-red-100 text-red-700' :
                              option.score <= 25 ? 'bg-orange-100 text-orange-700' :
                              option.score <= 50 ? 'bg-yellow-100 text-yellow-700' :
                              option.score <= 75 ? 'bg-blue-100 text-blue-700' :
                              'bg-green-100 text-green-700'
                            }`}>
                              {option.score}
                            </span>
                            <span className="text-gray-700">{option.text}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col gap-2">
                      <div className="flex gap-1">
                        <button
                          onClick={() => handleMoveQuestion(question._id, 'up')}
                          disabled={index === 0}
                          className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                          title="Monter"
                        >
                          <ChevronUp className="w-5 h-5 text-gray-600" />
                        </button>
                        <button
                          onClick={() => handleMoveQuestion(question._id, 'down')}
                          disabled={index === questions.length - 1}
                          className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                          title="Descendre"
                        >
                          <ChevronDown className="w-5 h-5 text-gray-600" />
                        </button>
                      </div>
                      <button
                        onClick={() => startEdit(question)}
                        className="p-2 hover:bg-teal-50 text-teal-600 rounded-lg transition-colors"
                        title="Modifier"
                      >
                        <Edit2 className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDeleteQuestion(question._id)}
                        className="p-2 hover:bg-red-50 text-red-600 rounded-lg transition-colors"
                        title="Supprimer"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
